// Formatting and shared vocabulary for the club reports.
//
// One rule runs through this file: a missing value is rendered as an em dash,
// never as 0. Most of what these reports show is a self-reported average, and a
// zero silently claims a child slept no hours or ate no food. "—" says we did
// not receive a record, which is the true and actionable statement.

export const NOT_RECORDED = "—";

/** Athletain's four-state wellness scale, mirroring the athlete app's
 *  STATUS_CONFIGS and the backend's WELLNESS_STATES. */
export const WELLNESS_STATES = [
  { min: 80, name: "Óptimo", color: "#0f8a6a" },
  { min: 61, name: "Precaución positiva", color: "#4274d9" },
  { min: 41, name: "Precaución negativa", color: "#fe694e" },
  { min: 0, name: "Alto riesgo", color: "#bc1d01" },
];

export const STATE_COLORS = {
  ok: "#0f8a6a",
  warn: "#fe694e",
  danger: "#f22601",
  neutral: "#41506e",
  muted: "#7692d1",
  missing: "#e3e0e0",
  accent: "#2351ac",
};

export const stateForScore = (score) =>
  WELLNESS_STATES.find((state) => score >= state.min) ?? WELLNESS_STATES[WELLNESS_STATES.length - 1];

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

/** Number with a fixed decimal count, or the not-recorded dash. */
export const fmtNumber = (value, decimals = 0) => {
  if (!isNumber(value)) return NOT_RECORDED;
  return value.toLocaleString("es-CO", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const fmtPercent = (value, decimals = 0) =>
  isNumber(value) ? `${fmtNumber(value, decimals)}%` : NOT_RECORDED;

/** Signed delta, so a reader can tell improvement from decline at a glance. */
export const fmtDelta = (value, decimals = 0) => {
  if (!isNumber(value)) return NOT_RECORDED;
  const formatted = fmtNumber(Math.abs(value), decimals);
  if (value === 0) return `0`;
  return `${value > 0 ? "+" : "−"}${formatted}`;
};

export const deltaColor = (value, { higherIsBetter = true } = {}) => {
  if (!isNumber(value) || value === 0) return STATE_COLORS.neutral;
  const good = higherIsBetter ? value > 0 : value < 0;
  return good ? STATE_COLORS.ok : STATE_COLORS.danger;
};

/** The acute:chronic ratio's accepted band. Outside it, the number is the point. */
export const ACWR_RANGE = { low: 0.8, high: 1.3, spike: 1.5 };

export const acwrColor = (value) => {
  if (!isNumber(value)) return STATE_COLORS.neutral;
  if (value > ACWR_RANGE.spike || value < ACWR_RANGE.low) return STATE_COLORS.danger;
  if (value > ACWR_RANGE.high) return STATE_COLORS.warn;
  return STATE_COLORS.ok;
};

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** "04 sep" — parsed as UTC so a YYYY-MM-DD never slips a day in a negative
 *  timezone offset, which is how date-only values usually go wrong. */
export const fmtShortDate = (iso) => {
  if (!iso) return NOT_RECORDED;
  const date = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return NOT_RECORDED;
  return `${String(date.getUTCDate()).padStart(2, "0")} ${MONTHS[date.getUTCMonth()]}`;
};

export const fmtDayNumber = (iso) => {
  if (!iso) return "";
  return String(iso).slice(8, 10);
};

export const fmtLongDate = (iso) => {
  if (!iso) return NOT_RECORDED;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return NOT_RECORDED;
  return date.toLocaleDateString("es-CO", { day: "2-digit", month: "long", year: "numeric" });
};

export const fmtPeriod = (period) =>
  period ? `${fmtShortDate(period.from)} – ${fmtShortDate(period.to)}` : NOT_RECORDED;

/** Normalised 0-100 position, guarded for the bar widths that consume it. */
export const barWidth = (value) => `${Math.max(0, Math.min(100, isNumber(value) ? value : 0))}%`;

export const hasValue = isNumber;
